import { useRef, useEffect, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "./ui/button";
import { Eraser } from "lucide-react";

/**
 * Reusable signature pad. Calls onChange(dataUrl) on every stroke end,
 * and onChange(null) when cleared. Drawing works on desktop (mouse) and tablet (touch/stylus).
 */
export default function SignaturePad({
  value,
  onChange,
  height = 160,
  disabled = false,
  label = "Firma del trabajador",
  placeholder = "Firme aqui con el dedo o lápiz óptico",
  testid = "signature-pad",
}) {
  const ref = useRef(null);
  const wrapperRef = useRef(null);
  const [empty, setEmpty] = useState(!value);

  // Adjust canvas size to container width on mount/resize
  useEffect(() => {
    const resize = () => {
      const canvas = ref.current?.getCanvas?.();
      const wrapper = wrapperRef.current;
      if (!canvas || !wrapper) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = wrapper.clientWidth * ratio;
      canvas.height = height * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
      // Redraw existing signature after resize (if any)
      if (value) {
        const img = new Image();
        img.onload = () => canvas.getContext("2d").drawImage(img, 0, 0, wrapper.clientWidth, height);
        img.src = value;
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  const handleEnd = () => {
    if (!ref.current) return;
    const isEmpty = ref.current.isEmpty();
    setEmpty(isEmpty);
    onChange(isEmpty ? null : ref.current.toDataURL("image/png"));
  };

  const handleClear = () => {
    ref.current?.clear();
    setEmpty(true);
    onChange(null);
  };

  return (
    <div data-testid={testid}>
      {label && (
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-zinc-700">{label}</span>
          {!empty && (
            <button
              type="button"
              onClick={handleClear}
              disabled={disabled}
              className="text-[11px] text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
              data-testid={`${testid}-clear`}
            >
              <Eraser className="w-3 h-3" /> Borrar
            </button>
          )}
        </div>
      )}
      <div
        ref={wrapperRef}
        className={`relative border rounded-lg bg-white ${
          disabled ? "opacity-50 pointer-events-none" : "border-zinc-300"
        }`}
        style={{ height }}
      >
        <SignatureCanvas
          ref={ref}
          penColor="#18181b"
          onEnd={handleEnd}
          canvasProps={{
            className: "w-full h-full rounded-lg",
            style: { touchAction: "none" },
          }}
        />
        {empty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-xs text-zinc-400 italic">{placeholder}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Simple read-only image display for a persisted signature. */
export function SignatureImage({ src, height = 80, className = "" }) {
  if (!src) {
    return (
      <div
        className={`flex items-center justify-center border-b border-zinc-300 ${className}`}
        style={{ height }}
      >
        <span className="text-[11px] text-zinc-400 italic">(Sin firmar)</span>
      </div>
    );
  }
  return (
    <div className={`border-b border-zinc-300 bg-white ${className}`} style={{ height }}>
      <img src={src} alt="Firma del trabajador" className="w-full h-full object-contain" />
    </div>
  );
}

/** Small helper to print a node by cloning it into an iframe (avoids breaking the app layout). */
export function printElement(node, title = "Recibo") {
  if (!node) return;
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);
  const doc = frame.contentDocument || frame.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>`);
  // Copy stylesheets from parent doc
  document.querySelectorAll('link[rel="stylesheet"], style').forEach((el) => {
    doc.write(el.outerHTML);
  });
  doc.write('<style>body{font-family:ui-sans-serif,system-ui,-apple-system,sans-serif;padding:24px;color:#18181b;background:#fff}</style>');
  doc.write("</head><body>");
  doc.write(node.outerHTML);
  doc.write("</body></html>");
  doc.close();
  setTimeout(() => {
    frame.contentWindow.focus();
    frame.contentWindow.print();
    setTimeout(() => document.body.removeChild(frame), 500);
  }, 250);
}
